import React from 'react';
import { Search } from 'lucide-react';

/**
 * FilterBar — one or more segmented tab groups + optional search input.
 *
 * groups: Array<{
 *   value:    string           — currently active key
 *   onChange: (key) => void   — called when a tab is clicked
 *   tabs:     Array<{
 *     key:    string
 *     label:  string
 *     Icon?:  LucideIcon
 *     count?: number
 *   }>
 * }>
 *
 * search?:            string
 * onSearch?:          (e) => void
 * searchPlaceholder?: string
 */
export function FilterBar({
  groups = [],
  search,
  onSearch,
  searchPlaceholder = 'Rechercher…',
  actions,
}) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        {groups.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && (
              <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
            )}
            <TabGroup group={group} />
          </React.Fragment>
        ))}
      </div>

      {(onSearch != null || actions) && (
        <div className="flex items-center gap-2">
          {onSearch != null && (
            <div className="relative">
              <Search
                size={13}
                strokeWidth={1.75}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={search ?? ''}
                onChange={onSearch}
                className="h-9 pl-7 pr-3 w-56 bg-white border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 placeholder:text-slate-400 transition"
              />
            </div>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}

function TabGroup({ group }) {
  const { value, onChange, tabs } = group;
  return (
    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
      {tabs.map(({ key, label, Icon, count }) => {
        const isActive = value === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition ${
              isActive
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {Icon && <Icon size={12} strokeWidth={1.75} />}
            {label}
            {count != null && (
              <span className={`text-[11px] ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default FilterBar;
